/**
 * Widget Configuration Validation
 * Contains validation rules and functions for widget configurations
 */

import { VALUE_FORMATS, LEGEND_POSITIONS } from './constants';

/**
 * Required and optional props for each widget type
 */
export const widgetPropSchema = {
  kpi: {
    requiredProps: ['title', 'format'],
    optionalProps: ['backgroundColor', 'trendData', 'showChange', 'style', 'totalValue']
  },

  table: {
    requiredProps: ['grouping'],
    optionalProps: ['title', 'valueName', 'onRowClick', 'showTitle', 'measures']
  },

  areachart: {
    requiredProps: ['title'],
    optionalProps: ['dateFormat', 'stacked', 'showButtons', 'showLegend', 'legendPosition', 'onFilterAdd']
  },

  barchart: {
    requiredProps: ['title'],
    optionalProps: ['valueName', 'dateFormat', 'showButtons', 'legendPosition', 'orientation']
  },

  donutchart: {
    requiredProps: ['title'],
    optionalProps: ['nameKey', 'valueKey', 'format', 'subtitle', 'showLegend']
  },

  sectionbreak: {
    requiredProps: [],
    optionalProps: ['title']
  },

  forecastchart: {
    requiredProps: ['title'],
    optionalProps: ['dateFormat', 'dateGrouping', 'showButtons', 'stacked', 'valueName', 'selectedSeries', 'availableSeries']
  },

  forecasttable: {
    requiredProps: ['title'],
    optionalProps: ['dateFormat', 'dateGrouping', 'grouping', 'measures']
  },

  resourcechangestrend: {
    requiredProps: ['title'],
    optionalProps: []
  },

  resourcechangesoverview: {
    requiredProps: ['title'],
    optionalProps: ['hideZeroNetChangeClusters', 'selectedClusters', 'clusterOrder']
  },

  resourceoverview: {
    requiredProps: ['title'],
    optionalProps: ['selectedClusters', 'clusterOrder']
  },

  resourcelist: {
    requiredProps: ['title'],
    optionalProps: ['displayLevel', 'selectedClusters', 'visibleColumns', 'visibleColumnsOrder']
  },

  resourceutilization: {
    requiredProps: ['title', 'selectedResourceId', 'selectedResourceName'],
    optionalProps: ['measure']
  },

  insighttable: {
    requiredProps: ['title'],
    optionalProps: ['showTitle', 'insightTypes', 'measures']
  },

  costtrendchart: {
    requiredProps: ['title'],
    optionalProps: ['grouping', 'dateFormat', 'stacked', 'showButtons', 'legendPosition', 'showLegend', 'dateGrouping']
  }
};

/**
 * Validate widget configuration against schema
 * @param {string} type - Widget type
 * @param {Object} config - Widget configuration
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateWidgetConfig = (type, config) => {
  const schema = widgetPropSchema[type];
  if (!schema) {
    return {
      isValid: false,
      errors: [`Unknown widget type: ${type}`]
    };
  }

  const errors = [];
  
  // Check required props
  for (const prop of schema.requiredProps) {
    if (!(prop in config)) {
      errors.push(`Missing required property: ${prop}`);
    } else if (config[prop] === null || config[prop] === undefined) {
      errors.push(`Required property '${prop}' cannot be null or undefined`);
    }
  }

  // Validate format values if present
  if (config.format && !VALUE_FORMATS.includes(config.format)) {
    errors.push(`Invalid format value: ${config.format}. Must be one of: ${VALUE_FORMATS.join(', ')}`);
  }

  // Validate legend position if present
  if (config.legendPosition && !LEGEND_POSITIONS.includes(config.legendPosition)) {
    errors.push(`Invalid legendPosition value: ${config.legendPosition}. Must be one of: ${LEGEND_POSITIONS.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get required props for a widget type
 * @param {string} type - Widget type
 * @returns {Array<string>} - Required props
 */
export const getRequiredProps = (type) => {
  return widgetPropSchema[type]?.requiredProps || [];
};

/**
 * Get optional props for a widget type
 * @param {string} type - Widget type
 * @returns {Array<string>} - Optional props
 */
export const getOptionalProps = (type) => {
  return widgetPropSchema[type]?.optionalProps || [];
};

export default validateWidgetConfig;

